// Reporte de Aguinaldo — Excel y PDF.
// Incluye: header institucional, tabla de empleados con totales y desglose mensual.
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { colones, fmtFecha } from "@/lib/api";

const sanitize = (s) => String(s || "").replace(/[^\w\s.-]/g, "_").replace(/\s+/g, "_").trim();
const filename = (year, ext) => `Reporte_Aguinaldo_${year}_${new Date().toISOString().slice(0, 10)}.${ext}`;

// ============================================================
// Excel
// ============================================================
export function reporteAguinaldoExcel({ empresa, year, filas }) {
  const wb = XLSX.utils.book_new();
  const total = filas.reduce((s, f) => s + (f.aguinaldo || 0), 0);
  const totalSalarios = filas.reduce((s, f) => s + (f.totalSalarios || 0), 0);
  const periodo = `01-Dic-${year - 1} al 30-Nov-${year}`;

  // ---- Hoja 1: Resumen ----
  const headerCellStyle = {
    font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "274735" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "999999" } },
      bottom: { style: "thin", color: { rgb: "999999" } },
      left: { style: "thin", color: { rgb: "999999" } },
      right: { style: "thin", color: { rgb: "999999" } },
    },
  };
  const rowStyle = (isEven, extra = {}) => ({
    font: { sz: 10 },
    fill: { fgColor: { rgb: isEven ? "FAFAF7" : "FFFFFF" } },
    alignment: { vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "E5E5E0" } },
      bottom: { style: "thin", color: { rgb: "E5E5E0" } },
      left: { style: "thin", color: { rgb: "E5E5E0" } },
      right: { style: "thin", color: { rgb: "E5E5E0" } },
    },
    ...extra,
  });

  const aoa = [
    [{ v: empresa?.razon_social || empresa?.nombre || "", s: { font: { italic: true, sz: 10, color: { rgb: "555555" } } } }],
    [{ v: `Reporte de Aguinaldo ${year}`, s: { font: { bold: true, sz: 16, color: { rgb: "274735" } } } }],
    [{ v: `Período: ${periodo}   ·   Generado: ${fmtFecha(new Date().toISOString().slice(0, 10))}`, s: { font: { sz: 10, color: { rgb: "666666" } } } }],
    [""],
    [
      { v: "#", s: headerCellStyle },
      { v: "Empleado", s: headerCellStyle },
      { v: "Cédula", s: headerCellStyle },
      { v: "Puesto", s: headerCellStyle },
      { v: "Total Salarios (12 meses)", s: headerCellStyle },
      { v: "Aguinaldo", s: headerCellStyle },
    ],
  ];

  filas.forEach((f, i) => {
    const isEven = i % 2 === 0;
    aoa.push([
      { v: i + 1, s: rowStyle(isEven, { alignment: { horizontal: "center", vertical: "center" } }) },
      { v: `${f.emp.nombre || ""} ${f.emp.apellidos || ""}`.trim(), s: rowStyle(isEven) },
      { v: f.emp.cedula || "—", s: rowStyle(isEven) },
      { v: f.emp.puesto || "—", s: rowStyle(isEven) },
      { v: f.totalSalarios || 0, s: { ...rowStyle(isEven, { alignment: { horizontal: "right", vertical: "center" } }), numFmt: '"₡"#,##0.00' } },
      { v: f.aguinaldo || 0, s: { ...rowStyle(isEven, { alignment: { horizontal: "right", vertical: "center" }, font: { bold: true, sz: 10, color: { rgb: "274735" } } }), numFmt: '"₡"#,##0.00' } },
    ]);
  });

  // Fila total
  const totalStyle = {
    font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "274735" } },
    alignment: { vertical: "center" },
  };
  aoa.push([
    { v: "", s: totalStyle },
    { v: "", s: totalStyle },
    { v: "", s: totalStyle },
    { v: "TOTAL", s: { ...totalStyle, alignment: { horizontal: "right", vertical: "center" } } },
    { v: totalSalarios, s: { ...totalStyle, alignment: { horizontal: "right", vertical: "center" }, numFmt: '"₡"#,##0.00' } },
    { v: total, s: { ...totalStyle, alignment: { horizontal: "right", vertical: "center" }, numFmt: '"₡"#,##0.00' } },
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 5 }, { wch: 32 }, { wch: 14 }, { wch: 24 }, { wch: 22 }, { wch: 18 }];
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Resumen");

  // ---- Hoja 2: Desglose Mensual ----
  const meses = filas[0]?.desglose?.map((m) => `${m.label}-${String(m.anio).slice(-2)}`) || [];
  const desHeader = [
    { v: "Empleado", s: headerCellStyle },
    { v: "Cédula", s: headerCellStyle },
    ...meses.map((m) => ({ v: m, s: headerCellStyle })),
    { v: "Total", s: headerCellStyle },
    { v: "Aguinaldo", s: headerCellStyle },
  ];
  const desglose = [
    [{ v: `Desglose Mensual — Aguinaldo ${year}`, s: { font: { bold: true, sz: 14, color: { rgb: "274735" } } } }],
    [""],
    desHeader,
  ];
  filas.forEach((f, i) => {
    const isEven = i % 2 === 0;
    const row = [
      { v: `${f.emp.nombre || ""} ${f.emp.apellidos || ""}`.trim(), s: rowStyle(isEven) },
      { v: f.emp.cedula || "—", s: rowStyle(isEven) },
      ...(f.desglose || []).map((m) => ({
        v: m.salario || 0,
        s: { ...rowStyle(isEven, { alignment: { horizontal: "right", vertical: "center" } }), numFmt: '"₡"#,##0.00' },
      })),
      { v: f.totalSalarios || 0, s: { ...rowStyle(isEven, { alignment: { horizontal: "right", vertical: "center" }, font: { bold: true } }), numFmt: '"₡"#,##0.00' } },
      { v: f.aguinaldo || 0, s: { ...rowStyle(isEven, { alignment: { horizontal: "right", vertical: "center" }, font: { bold: true, color: { rgb: "274735" } } }), numFmt: '"₡"#,##0.00' } },
    ];
    desglose.push(row);
  });
  const ws2 = XLSX.utils.aoa_to_sheet(desglose);
  ws2["!cols"] = [{ wch: 30 }, { wch: 14 }, ...meses.map(() => ({ wch: 13 })), { wch: 16 }, { wch: 16 }];
  ws2["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: meses.length + 3 } }];
  XLSX.utils.book_append_sheet(wb, ws2, "Desglose Mensual");

  XLSX.writeFile(wb, filename(year, "xlsx"));
}

// ============================================================
// PDF
// ============================================================
export function reporteAguinaldoPDF({ empresa, year, filas }) {
  const total = filas.reduce((s, f) => s + (f.aguinaldo || 0), 0);
  const totalSalarios = filas.reduce((s, f) => s + (f.totalSalarios || 0), 0);
  const periodo = `01-Dic-${year - 1} al 30-Nov-${year}`;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 32;

  // Header institucional
  doc.setFillColor(39, 71, 53);
  doc.rect(0, 0, pageWidth, 72, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(`Reporte de Aguinaldo ${year}`, marginX, 32);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Período: ${periodo}`, marginX, 50);
  doc.setFontSize(9);
  doc.setTextColor(220, 230, 224);
  doc.text(`${empresa?.razon_social || empresa?.nombre || ""}   ·   Generado: ${fmtFecha(new Date().toISOString().slice(0, 10))}`, marginX, 64);

  // Tabla principal
  autoTable(doc, {
    startY: 92,
    head: [["#", "Empleado", "Cédula", "Puesto", "Total Salarios (12m)", "Aguinaldo"]],
    body: filas.map((f, i) => [
      i + 1,
      `${f.emp.nombre || ""} ${f.emp.apellidos || ""}`.trim(),
      f.emp.cedula || "—",
      f.emp.puesto || "—",
      colones(f.totalSalarios || 0),
      colones(f.aguinaldo || 0),
    ]),
    foot: [[
      { content: "TOTAL", colSpan: 4, styles: { halign: "right", fontStyle: "bold", fillColor: [39, 71, 53], textColor: 255 } },
      { content: colones(totalSalarios), styles: { halign: "right", fontStyle: "bold", fillColor: [39, 71, 53], textColor: 255 } },
      { content: colones(total), styles: { halign: "right", fontStyle: "bold", fillColor: [39, 71, 53], textColor: 255 } },
    ]],
    theme: "grid",
    headStyles: { fillColor: [39, 71, 53], textColor: 255, fontStyle: "bold", fontSize: 9.5 },
    styles: { fontSize: 9, cellPadding: 5, lineColor: [220, 220, 210], lineWidth: 0.4 },
    columnStyles: {
      0: { halign: "center", cellWidth: 30 },
      4: { halign: "right" },
      5: { halign: "right", fontStyle: "bold" },
    },
    alternateRowStyles: { fillColor: [250, 250, 247] },
    margin: { left: marginX, right: marginX },
  });

  // Nueva página: desglose mensual
  const meses = filas[0]?.desglose?.map((m) => `${m.label.slice(0, 3)}-${String(m.anio).slice(-2)}`) || [];
  if (meses.length > 0 && filas.length > 0) {
    doc.addPage();
    doc.setFillColor(39, 71, 53);
    doc.rect(0, 0, pageWidth, 40, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(`Desglose Mensual — Aguinaldo ${year}`, marginX, 26);

    autoTable(doc, {
      startY: 56,
      head: [["Empleado", ...meses, "Total", "Aguinaldo"]],
      body: filas.map((f) => [
        `${f.emp.nombre || ""} ${f.emp.apellidos || ""}`.trim(),
        ...(f.desglose || []).map((m) => colones(m.salario || 0)),
        colones(f.totalSalarios || 0),
        colones(f.aguinaldo || 0),
      ]),
      theme: "grid",
      headStyles: { fillColor: [39, 71, 53], textColor: 255, fontStyle: "bold", fontSize: 7.5 },
      styles: { fontSize: 7, cellPadding: 3, lineColor: [220, 220, 210], lineWidth: 0.3, overflow: "linebreak" },
      alternateRowStyles: { fillColor: [250, 250, 247] },
      columnStyles: {
        0: { cellWidth: 100 },
        [meses.length + 1]: { halign: "right", fontStyle: "bold" },
        [meses.length + 2]: { halign: "right", fontStyle: "bold", textColor: [39, 71, 53] },
      },
      margin: { left: marginX, right: marginX },
    });
  }

  // Footer paginación
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - marginX, doc.internal.pageSize.getHeight() - 16, { align: "right" });
  }

  doc.save(filename(year, "pdf"));
}
